import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';
import ShipScheduleDelaysService from '../../../../../../services/data/ships/shipScheduleDelays';

// props
import { IShipScheduleDelayResourceShort } from '../../../../../../props/data/ships';

// components
import { CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../typography/text';
import ShipScheduleDelayForm from '../form';

interface IShipScheduleDelaysListProps {
    scheduleId: string;
}

const ShipScheduleDelaysList: React.FC<IShipScheduleDelaysListProps> = (props: IShipScheduleDelaysListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [scheduleDelays, setScheduleDelays] = React.useState<IShipScheduleDelayResourceShort[]>([]);
    const [activeScheduleDelay, setActiveScheduleDelay] = React.useState<IShipScheduleDelayResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "createdAt",
            name: "Created At",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IShipScheduleDelayResourceShort) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{moment(item.createdAt).format("DD/MM/YYYY")}</Text>
                </Stack>
            }
        },
        {
            key: "departureDate",
            name: "New Departure Date",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IShipScheduleDelayResourceShort) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{moment(item.newEstimatedDeparture).format("DD/MM/YYYY")}</Text>
                </Stack>
            }
        },
        {
            key: "arrivalDate",
            name: "New Arrival Date",
            minWidth: 200,
            maxWidth: 200,
            isMultiline: true,
            onRender: (item: IShipScheduleDelayResourceShort) => {
                return <Stack horizontal tokens={{ childrenGap: 5 }} styles={{ root: { padding: '4px 0px' } }}>
                    <Text>{moment(item.newEstimatedArrival).format("DD/MM/YYYY")}</Text>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        _onRetrieveScheduleDelays();
    }, [keyword]);

    const _onRetrieveScheduleDelays = async () => {
        try {
            const qs: string[] = [];
            qs.push('schedule_id=' + props.scheduleId);

            const results = await ShipScheduleDelaysService.retrieve(qs.join("&"));
            setScheduleDelays(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_ship_schedule_delays_error',
                text: `Failed to retrieve ship's schedule delays list. Error: ` + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "register",
            text: "Delay Schedule",
            iconProps: { iconName: "Add" },
            onRender: () => {
                return <PrimaryButton text={"Delay Schedule"}
                    iconProps={{ iconName: "Add" }}
                    onClick={() => { setActiveSurface('create') }}
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveScheduleDelay(undefined);

        if (refresh) { _onRetrieveScheduleDelays() }
    }

    return <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 10 } }}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack>
            {
                !loaded || (loaded && scheduleDelays.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={scheduleDelays}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && scheduleDelays.length < 1 ? <Text>Ship schedule(s) not found</Text> : null}
        </Stack>
        {activeSurface === 'create' ? <ShipScheduleDelayForm scheduleId={props.scheduleId} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default ShipScheduleDelaysList;